import { PersonAPI } from '@frontend/api-person';
import { createStoreWithSubscribe, createShallowStore } from '@frontend/store';

export interface PhotoStore {
  photoRecord: Record<string, string>;
  setPhotoRecord: (id: string, imgUrl: string) => void;
  handleGetPicture: (id: string) => Promise<string | undefined>;
  handleGetPictures: (ids: string[]) => Promise<void>;
  pendingRequests: Record<string, Promise<string> | undefined>;
}

export const usePhotoStore = createStoreWithSubscribe<PhotoStore>(
  (set, get) => ({
    photoRecord: {},
    pendingRequests: {},
    setPhotoRecord: (personId: string, imgUrl: string) => {
      set((state) => {
        state.photoRecord[personId] = imgUrl;
      });
    },
    handleGetPicture: async (id: string) => {
      const photo = get().photoRecord[id];
      if (photo !== undefined) {
        return photo;
      }

      // Only proceed if there is no pending request for this id
      if (get().pendingRequests[id]) return;

      const promise = PersonAPI.getPersonImage(id)
        .then((photoData) => {
          set(
            (state) => {
              state.photoRecord[id] = photoData;
              delete state.pendingRequests[id];
            },
            false,
            'SET_SHOW_CONTACT_PANEL'
          );
          return photoData;
        })
        .catch(() => {
          set(
            (state) => {
              state.photoRecord[id] = '';
              delete state.pendingRequests[id];
            },
            false,
            'SET_SHOW_CONTACT_PANEL'
          );
          return '';
        });

      set((state) => {
        state.pendingRequests[id] = promise;
      });

      return promise;
    },
    handleGetPictures: async (ids: string[]) => {
      const uncachedIds = ids.filter((id) => get().photoRecord[id] === undefined && !get().pendingRequests[id]);

      if (uncachedIds.length === 0) return;

      // Fire all requests and mark as pending in one synchronous set() — no
      // window where a request is in-flight but not yet tracked as pending.
      const promises: Promise<{ id: string; photo: string }>[] = [];
      set((state) => {
        for (const id of uncachedIds) {
          const promise = PersonAPI.getPersonImage(id)
            .then((photo) => ({ id, photo }))
            .catch(() => ({ id, photo: '' }));
          promises.push(promise);
          state.pendingRequests[id] = promise.then(({ photo }) => photo);
        }
      });

      const results = await Promise.all(promises);

      // Single batched state update — one store notification instead of N.
      set(
        (state) => {
          for (const { id, photo } of results) {
            state.photoRecord[id] = photo;
            delete state.pendingRequests[id];
          }
        },
        false,
        'BATCH_SET_PHOTOS'
      );
    },
  }),
  { name: 'PhotoStore', trace: true }
);

export const usePhotoShallowStore = createShallowStore(usePhotoStore);
